import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, MapPin, Briefcase, Calendar, Banknote,
  Clock, CheckCircle, Edit, Ban, ArrowLeft, Users,
  BarChart, CalendarDays, Laptop, Star, GraduationCap, Target, Sparkles, Search, X
} from "lucide-react";
import JobOpportunityClient, { JobOpportunity } from "../data/api/JobOpportunityClient";
import JobDashboardClient, { OpportunityMetrics } from "../../jobDashboard/data/api/JobDashboardClient";
import JobApplicationsKanbanPage from "../../jobApplications/pages/JobApplicationsKanbanPage";
import JobInterviewsTab from "../components/JobInterviewsTab";
import JobAnalyticsTab from "../components/JobAnalyticsTab";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";

const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#ef4444', '#14b8a6', '#f43f5e', '#84cc16'];

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case "Open":
      return "jo-badge-green";
    case "Closed":
      return "jo-badge-gray";
    case "Draft":
      return "jo-badge-amber";
    default:
      return "jo-badge-gray";
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const JobDetailPage: React.FC = () => {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [job, setJob] = useState<JobOpportunity | null>(null);
  const [metrics, setMetrics] = useState<OpportunityMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialTab = searchParams.get("tab") === "applications" ? "applications" as const : "overview" as const;
  const [activeTab, setActiveTab] = useState<"overview" | "applications" | "interviews" | "analytics">(initialTab);
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const [showProspectiveToast, setShowProspectiveToast] = useState(true);

  const hasProspects = job?.has_prospective_candidates === true;
  const prospectCount = job?.prospective_count;
  // Explicit zero never notifies; unknown (undefined/null) still does.
  const showProspect = hasProspects && prospectCount !== 0;

  useEffect(() => {
    if (showProspect) setShowProspectiveToast(true);
  }, [showProspect, opportunityId]);

  useEffect(() => {
    if (!opportunityId) return;
    setLoading(true);
    Promise.all([
      JobOpportunityClient.getOpportunity(opportunityId),
      JobDashboardClient.getMetrics(opportunityId).catch(() => null),
    ])
      .then(([jobData, metricsData]) => {
        setJob(jobData);
        if (metricsData) setMetrics(metricsData);
      })
      .catch((err) => setError(err.message || "Failed to load job details"))
      .finally(() => setLoading(false));
  }, [opportunityId]);

  // Refresh the flag when coming back to Overview (it flips False once all
  // prospects are moved to applied in the Applications tab).
  useEffect(() => {
    if (activeTab !== "overview" || !opportunityId) return;
    JobOpportunityClient.getOpportunity(opportunityId)
      .then((jobData) => setJob(jobData))
      .catch(() => null);
  }, [activeTab, opportunityId]);

  const handleCloseJobClick = () => {
    if (!job || job.status === "Closed") return;
    setShowCloseAlert(true);
  };

  const confirmCloseJob = async () => {
    if (!job) return;
    setShowCloseAlert(false);
    try {
      await JobOpportunityClient.updateOpportunity(job.opportunity_id!, { status: "Closed" });
      setJob({ ...job, status: "Closed" });
    } catch (err: any) {
      alert(err.message || "Failed to close job.");
    }
  };

  if (loading) return <Loader isLoading={true} message="Loading job details..." />;
  if (error || !job) return <div className="p-8 text-center text-red-500">{error || "Job not found"}</div>;

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Alert
        show={showCloseAlert}
        type="warning"
        title="Close Job Opportunity"
        message="Are you sure you want to close this job opportunity? No new applications will be accepted."
        primaryButtonText="Close Job"
        onPrimaryClick={confirmCloseJob}
        secondaryButtonText="Cancel"
        onSecondaryClick={() => setShowCloseAlert(false)}
        onClose={() => setShowCloseAlert(false)}
      />
      {/* Hero Section */}
      <div className="jo-detail-hero">
        <div className="jo-detail-hero-inner">
          {/* Left: back + title + badge + subtitle */}
          <div className="jo-detail-hero-left">
            <button className="jo-back-btn-inline" onClick={() => navigate("/dashboard/job-opportunities")} title="Back to Jobs">
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="jo-detail-title-row">
                <h1>{job.title}</h1>
                <span className={`jo-badge ${statusBadgeClass(job.status)}`}>
                  {job.status || "Draft"}
                </span>
              </div>
              <div className="jo-detail-subtitle">
                <div className="flex items-center gap-2">
                  <Building2 size={13} />
                  <span>{job.for_organization_name || job.company_name || "Internal"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={13} />
                  <span>Vacancies: <strong>{job.vacancies || "—"}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={13} />
                  <span>Deadline: <strong>{formatDate(job.application_deadline) || "—"}</strong></span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: action buttons */}
          <div className="jo-detail-hero-right">
            <button className="jo-btn-outline" onClick={() => navigate(`/dashboard/job-opportunity/edit/${job.opportunity_id}`)}>
              <Edit size={16} /> Edit Job
            </button>
            {job.status !== "Closed" && (
              <button className="jo-btn-danger-outline" onClick={handleCloseJobClick}>
                <Ban size={16} /> Close Job
              </button>
            )}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="jo-tabs">
        <button className={`jo-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
          Overview
        </button>
        <button className={`jo-tab ${activeTab === "applications" ? "active" : ""}`} onClick={() => setActiveTab("applications")}>
          Applications
        </button>
        <button className={`jo-tab ${activeTab === "interviews" ? "active" : ""}`} onClick={() => setActiveTab("interviews")}>
          Interviews
        </button>
        <button className={`jo-tab ${activeTab === "analytics" ? "active" : ""}`} onClick={() => setActiveTab("analytics")}>
          Analytics
        </button>

        <AnimatePresence>
          {showProspect && activeTab !== "applications" && showProspectiveToast && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: -8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.9, x: -8 }}
              transition={{ duration: 0.2 }}
              className="jo-tab-prospect-pill"
              onClick={() => setActiveTab("applications")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setActiveTab("applications");
                }
              }}
              title="Click to review prospective candidates in Applications"
            >
              <div className="jo-tab-prospect-icon">
                <Sparkles size={13} />
              </div>
              <div className="jo-tab-prospect-text">
                <span className="jo-tab-prospect-title">Talent Search Results</span>
                <span className="jo-tab-prospect-divider">—</span>
                <span className="jo-tab-prospect-desc">
                  {(prospectCount ?? 0) > 0 ? (
                    <>
                      <strong>{prospectCount}</strong> prospective candidate{prospectCount !== 1 ? 's' : ''} found.
                    </>
                  ) : (
                    <>Prospective candidates found.</>
                  )}
                </span>
              </div>
              <button
                type="button"
                className="jo-tab-prospect-action"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab("applications");
                }}
              >
                Review
              </button>
              <button
                type="button"
                className="jo-tab-prospect-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProspectiveToast(false);
                }}
                aria-label="Dismiss notification"
              >
                <X size={13} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tab Content */}
      <div className={`jo-tab-content${activeTab === "applications" ? " jo-tab-content--kanban" : ""}`}>
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              <div className="jo-overview-grid">

                {/* Left Column */}
                <div className="jo-overview-main" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="jo-detail-card">
                    <h3 className="jo-card-title mb-4">Job Description</h3>
                    {job.description ? (
                      <div className="jo-description" dangerouslySetInnerHTML={{ __html: job.description }} />
                    ) : (
                      <div className="text-gray-400 italic">No description provided.</div>
                    )}
                  </div>

                  {(job.skills_required?.length > 0 || job.skills_preferred?.length > 0) && (
                    <div className="jo-detail-card">
                      <h3 className="jo-card-title mb-4">Skills &amp; Technologies</h3>
                      <div className="jo-skills-container">
                        {job.skills_required?.length > 0 && (
                          <div className="jo-skills-group">
                            <h4 className="jo-skills-group-title required-title">
                              <span className="dot"></span> Required
                            </h4>
                            <div className="jo-skills-list">
                              {job.skills_required.map((skill, i) => (
                                <span key={i} className="jo-skill-badge required">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {job.skills_preferred?.length > 0 && (
                          <>
                            {job.skills_required?.length > 0 && <div className="jo-skills-divider"></div>}
                            <div className="jo-skills-group">
                              <h4 className="jo-skills-group-title preferred-title">
                                <span className="dot"></span> Preferred
                              </h4>
                              <div className="jo-skills-list">
                                {job.skills_preferred.map((skill, i) => (
                                  <span key={i} className="jo-skill-badge preferred">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="jo-overview-side" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

                  {metrics && metrics.stage_counts && (() => {
                    const stageList = job.stages?.length ? job.stages : ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];
                    const pipelineData = stageList.map(stage => ({
                      stage,
                      count: metrics.stage_counts?.[stage] || 0
                    }));
                    const maxPipeline = Math.max(...pipelineData.map(d => d.count), 1);
                    return (
                      <div className="jo-detail-card">
                        <h3 className="jo-card-title mb-4">Pipeline Overview</h3>
                        <div className="custom-pipeline-container">
                          {pipelineData.map((item, index) => (
                            <div key={item.stage} className="pipeline-progress-row">
                              <div className="pipeline-label-row">
                                <span className="pipeline-stage">{item.stage}</span>
                                <span className="pipeline-count">{item.count.toLocaleString()}</span>
                              </div>
                              <div className="pipeline-track">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${(item.count / maxPipeline) * 100}%` }}
                                  transition={{ duration: 0.9, ease: 'easeOut', delay: index * 0.05 }}
                                  className="pipeline-fill"
                                  style={{ background: COLORS[index % COLORS.length] }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="jo-detail-card">
                    <h3 className="jo-card-title mb-4">Logistics &amp; Compensation</h3>
                    <div className="jo-details-list">
                      {job.department && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Briefcase size={14} /> Department</div>
                          <div className="jo-detail-val">{job.department}</div>
                        </div>
                      )}
                      {job.work_location && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><MapPin size={14} /> Location</div>
                          <div className="jo-detail-val">{job.work_location}</div>
                        </div>
                      )}
                      {job.hiring_type && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><CheckCircle size={14} /> Hiring Type</div>
                          <div className="jo-detail-val">{job.hiring_type}</div>
                        </div>
                      )}
                      {job.payment_type && (job.min_ctc || job.max_ctc || job.min_hourly_rate) && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Banknote size={14} /> Compensation</div>
                          <div className="jo-detail-val highlight">
                            {job.payment_type === "Hourly"
                              ? `$${(job.min_hourly_rate || 0).toLocaleString()} - $${(job.max_hourly_rate || 0).toLocaleString()}/hr`
                              : `$${(job.min_ctc || 0).toLocaleString()} - $${(job.max_ctc || 0).toLocaleString()}`}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="jo-detail-card">
                    <h3 className="jo-card-title mb-4">Role Requirements</h3>
                    <div className="jo-details-list">
                      {job.job_type && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Clock size={14} /> Job Type</div>
                          <div className="jo-detail-val">{job.job_type}</div>
                        </div>
                      )}
                      {job.work_type && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Laptop size={14} /> Work Type</div>
                          <div className="jo-detail-val">{job.work_type}</div>
                        </div>
                      )}
                      {(job.min_years_experience !== undefined || job.max_years_experience !== undefined) && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Star size={14} /> Experience</div>
                          <div className="jo-detail-val">
                            {job.min_years_experience || 0}{job.max_years_experience ? ` - ${job.max_years_experience}` : '+'} years
                          </div>
                        </div>
                      )}
                      {job.education_level && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><GraduationCap size={14} /> Education</div>
                          <div className="jo-detail-val">{job.education_level}</div>
                        </div>
                      )}
                      {job.vacancies && (
                        <div className="jo-detail-row">
                          <div className="jo-detail-label"><Target size={14} /> Vacancies</div>
                          <div className="jo-detail-val">{job.vacancies}</div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>
          )}

          {activeTab === "applications" && (
            <motion.div key="applications" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} style={{ height: "100%" }}>
              <JobApplicationsKanbanPage isEmbedded={true} />
            </motion.div>
          )}

          {activeTab === "interviews" && (
            <motion.div key="interviews" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              <JobInterviewsTab jobOpportunityId={opportunityId!} />
            </motion.div>
          )}

          {activeTab === "analytics" && (
            <motion.div key="analytics" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} style={{ height: '100%', paddingBottom: '16px' }}>
              {metrics ? (
                <JobAnalyticsTab
                  metrics={metrics}
                  stages={job?.stages}
                  job={job}
                  opportunityId={opportunityId}
                />
              ) : (
                <div className="jo-empty-tab">
                  <BarChart size={48} color="#cbd5e1" />
                  <h3>Analytics Unavailable</h3>
                  <p>Could not load metrics for this job opportunity.</p>
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>


    </motion.div>
  );
};

export default JobDetailPage;
